// ============================================================================
// PairsForm - Types de Navigation (v2 - rôles multiples)
// ============================================================================

import type { LucideIcon } from "lucide-react";
import type { UserRole } from "@cecodev/shared";

// ----------------------------------------------------------------------------
// Menu
// ----------------------------------------------------------------------------

export interface MenuItem {
  id: string;
  label: string;
  href: string;
  icon?: LucideIcon;
  disabled?: boolean;
  badge?: number;
  /**
   * Membership à activer pour que cette entrée fonctionne, quand ce n'est PAS
   * l'org active (bascule implicite, FX 2026-08-19). Renseigné uniquement sur
   * les espaces PERSONNELS : une personne peut être participante d'une org et
   * animatrice d'une autre, et son menu ne doit pas dépendre de laquelle est
   * active. `undefined` = l'org active suffit. Cf. `useCasquetteImplicite`.
   */
  membershipId?: string;
}

export interface MenuSection {
  id: string;
  title: string;
  icon: LucideIcon;
  href?: string;
  items?: MenuItem[];
  separator?: boolean;
  /** Badge d'état sur une section à lien direct (ex. Tableau de bord → nb d'actions bloquantes). */
  badge?: number;
}

// ----------------------------------------------------------------------------
// Context de Navigation
// ----------------------------------------------------------------------------

export interface NavContext {
  role: UserRole; // legacy — rôle principal
  roles?: UserRole[]; // rôles multiples
  organisationId?: string;
  organisationNom?: string;
  organisationType?: string;
  /** Slug de l'org du jeton (lot 9.1) : segment des liens d'organisation du menu (/o/:slug), repli sur organisationId. */
  organisationSlug?: string | null;
  estAnimateur?: boolean;
  estParticipant?: boolean;
  typeAnimateur?: string | null;
  plateformeActif?: boolean;
  /** S2 Chargé d'affaire : au moins un compte client affecté (l'entrée
   *  « Mes comptes clients » n'apparaît que dans ce cas — retour FX). */
  aComptesClientsAffectes?: boolean;
  /** Divulgation progressive : au moins une inscription (cf. UserSession). */
  aInscriptions?: boolean;
  /**
   * Niveau reconnu à l'annuaire des certifications (Module Vert → CERTIFICATION,
   * Module Rouge → ACCREDITATION), `null` si la personne n'en a aucun.
   *
   * Sert de gate à l'espace ANIMATEUR : sans badge, on ne peut pas animer en
   * Inter, donc lui proposer d'y candidater est une invitation qu'il ne peut
   * pas honorer (retour FX 2026-08-19 sur un participant B2C).
   */
  niveauCertification?: "CERTIFICATION" | "ACCREDITATION" | "EXPERT_FORMATEUR" | null;
  /**
   * Parcours e-learning ouverts (lot 5 certifications) : badge ≥ parcours ou
   * Module commencé, à vie — porte l'entrée « E-Learning » quelle que soit
   * l'org active (cf. UserSession.parcoursElearning).
   */
  parcoursElearning?: { vert: boolean; rouge: boolean };
  /**
   * Permissions de CHACUNE des casquettes de la personne (une par membership),
   * calculées serveur-side pour l'org de chaque membership. Sert à la
   * VISIBILITÉ des espaces personnels — jamais à autoriser un geste, qui reste
   * gouverné par `allowedPermissions` (l'org du JWT). Cf. `usePermissions`.
   */
  casquettes?: ReadonlyArray<{
    membershipId: string;
    organisationId: string;
    organisationNom: string | null;
    organisationType: string;
    roles: string[];
    permissions: ReadonlySet<string>;
  }>;
  /** Membership porté par le JWT courant (pour ne pas basculer vers soi-même). */
  activeMembershipId?: string;
  /**
   * Sprint 8 · B5 — Permissions effectives (matrice + court-circuit
   * SUPER_ADMIN). Renseigné par useNavigation depuis usePermissions.
   * Helper recommandé pour le tester dans menu-config : `can(ctx, key)`.
   */
  allowedPermissions?: ReadonlySet<string>;
}

export interface RecentProject {
  id: string;
  nom: string;
}

// ----------------------------------------------------------------------------
// Helpers de rôles (supportent roles array)
// ----------------------------------------------------------------------------

/** Récupère la liste de rôles depuis le contexte */
function getRoles(ctx: { role?: UserRole; roles?: UserRole[] }): UserRole[] {
  if (ctx.roles && ctx.roles.length > 0) return ctx.roles;
  if (ctx.role) return [ctx.role];
  return [];
}

function isPiloteOuPlus(role: UserRole, roles?: UserRole[]): boolean {
  const all = roles && roles.length > 0 ? roles : [role];
  return all.some((r) => r === "SUPER_ADMIN" || r === "PILOTE");
}

export function isAdmin(role: UserRole, roles?: UserRole[]): boolean {
  return isPiloteOuPlus(role, roles);
}

export function isAnimateur(role: UserRole, roles?: UserRole[]): boolean {
  const all = roles && roles.length > 0 ? roles : [role];
  return all.includes("ANIMATEUR");
}


export function hasRole(targetRole: UserRole, role: UserRole, roles?: UserRole[]): boolean {
  const all = roles && roles.length > 0 ? roles : [role];
  return all.includes(targetRole);
}

/**
 * Labels des rôles
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: "Super Admin",
  PILOTE: "Pilote",
  ANIMATEUR: "Animateur",
  PARTICIPANT: "Participant",
};
